import { Link } from "react-router-dom";
import { useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { CALLABLES } from "@/services/callables";
import { consentCoverage } from "./rules";
import type { Group, RosterEntry } from "@/types";

/**
 * Consent coverage, on the group.
 *
 * The same condition the app enforces when a teacher tries to start a lesson.
 * Surfacing it here means they meet it while planning rather than while
 * standing in front of a class, and it names who is missing — three names are
 * actionable in a way that "three missing" is not.
 */
export function ConsentBanner({ group }: { group: Group }) {
  const t = useT();
  const readiness = useCallable<
    { consent: { required: boolean; recorded: number; missing: RosterEntry[] } },
    { groupId: string }
  >(CALLABLES.sessionReadiness, { groupId: group.id });

  if (!readiness.data) return null;

  const coverage = consentCoverage(group, readiness.data.consent);
  if (!coverage.required) return null;

  return (
    <div
      role="status"
      className={`mt-4 rounded-card border px-5 py-4 ${
        coverage.complete
          ? "border-positive bg-positive-soft"
          : "border-danger bg-danger-soft"
      }`}
    >
      <p className="text-sm font-medium text-ink">
        {t("group.consent.title")}
        <span className="ml-2 font-normal text-ink-soft">
          {t("group.consent.coverage", {
            recorded: coverage.recorded,
            total: coverage.total,
          })}
        </span>
      </p>

      {coverage.complete ? (
        <p className="mt-1 text-sm text-ink-soft">{t("group.consent.complete")}</p>
      ) : (
        <>
          <p className="mt-1 text-sm text-ink-soft">
            {t("group.consent.missing", {
              names: coverage.missing.map((entry) => entry.displayName).join(", "),
            })}
          </p>
          <p className="mt-1 text-sm text-ink-soft">{t("group.consent.blocks")}</p>
          <Link
            to="/consent"
            className="mt-3 inline-block text-sm font-medium text-danger underline underline-offset-2"
          >
            {t("group.consent.record")}
          </Link>
        </>
      )}
    </div>
  );
}
